'use client';

// TO DO: Add metadata: sketch title by author, image, etc

import { useState, useEffect, useMemo } from 'react';
import { useSession } from 'next-auth/react';
import { useParams, useRouter } from 'next/navigation';
import { FullControlEditor } from '@/app/components';
import { ControlDefinition } from '@/types/api';
import { isTauri } from '@/lib/runtime-bridge';
import { desktopService } from '@/lib/desktop-service';

interface Sketch {
  id: string;
  title: string;
  description?: string;
  code: string;
  svgThumbnail?: string;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
  views: number;
  likes: number;
  tags: string[];
  controls?: ControlDefinition[];
  author: {
    id: string;
    name?: string;
    username?: string;
    image?: string;
  };
}

export default function SketchDetailPage() {
  const { data: session } = useSession();
  const params = useParams();
  const router = useRouter();
  const sketchId = params.sketchId as string;

  const [sketch, setSketch] = useState<Sketch | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchSketch();
  }, [sketchId]);

  const fetchSketch = async () => {
    if (isTauri()) {
      try {
        const localSketch = await desktopService.loadSketch(sketchId);
        if (localSketch) {
          setSketch({
            ...localSketch,
            author: { id: 'local', username: 'You' },
            isPublic: false,
            views: 0,
            likes: 0,
            tags: []
          });
        } else {
          setError('Sketch not found locally');
        }
      } catch (err) {
        setError('Failed to load local sketch');
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      const response = await fetch(`/api/sketches/${sketchId}`);
      const data = await response.json();

      if (data.success) {
        setSketch(data.sketch);
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Failed to load sketch');
    } finally {
      setLoading(false);
    }
  };

  // Memoize computed values to prevent unnecessary rerenders
  const initialData = useMemo(() =>
    sketch ? {
      id: sketch.id,
      title: sketch.title,
      description: sketch.description,
      code: sketch.code,
      svgThumbnail: sketch.svgThumbnail,
      isPublic: sketch.isPublic,
      createdAt: sketch.createdAt,
      updatedAt: sketch.updatedAt,
      views: sketch.views,
      likes: sketch.likes,
      tags: sketch.tags,
      controls: sketch.controls,
      author: sketch.author
    } : undefined,
    [sketch]
  );
  const isOwner = useMemo(() => session?.user?.id === sketch?.author.id, [session, sketch]);
  const canEdit = isTauri() || isOwner;
  const forkSketch = useMemo(() => () => {
    if (!session) {
      router.push('/auth/signin?callbackUrl=' + encodeURIComponent(window.location.href));
      return;
    }
    const params = new URLSearchParams({
      code: sketch?.code || '',
      title: `Fork of ${sketch?.title}`,
    });
    router.push(`/create?${params.toString()}`);
  }, [session, router, sketch]);

  if (loading) {
    return <div>Loading...</div>;
  }
  if (error) {
    return <div>Error: {error}</div>;
  }
  if (!sketch) {
    return <div>No sketch found.</div>;
  }
  return (
    <FullControlEditor
      mode="view"
      initialData={initialData}
      loading={loading}
      error={error}
      canEdit={canEdit}
      showForkButton={!isOwner}
      onFork={forkSketch}
    />
  );
}
